import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useYDocStore from '../../stores/useYDocStore';
import {getAllReferences, observeReferences} from '../prosemirror-editor/yMaps/referencesYMap';

export default function useCitedHighlights(reportId, currentDocKey, segments) {
    const yReferenceMarks = useYDocStore(s => s.yReferenceMarks);

    // Reactive snapshot — re-read on every YJS change
    const [version, setVersion] = useState(0);

    useEffect(() => {
        if (!yReferenceMarks) return;
        // Read once immediately to populate on mount
        setVersion(v => v + 1);
        return observeReferences(yReferenceMarks, () => setVersion(v => v + 1));
    }, [yReferenceMarks]);

    // Use a ref so segment content is available without re-subscribing
    const segmentsRef = useRef(segments);
    segmentsRef.current = segments;

    const citedHighlights = useMemo(() => {
        if (!yReferenceMarks || !currentDocKey) return [];

        const allRefs = getAllReferences(yReferenceMarks); // Map<markId, ref[]>
        const items = [];

        allRefs.forEach((refs, referenceMarkId) => {
            refs.forEach(r => {
                if (r?.docId == null) return;
                // Match by docId — support both numeric and string keys
                // eslint-disable-next-line eqeqeq
                if (r.docId != currentDocKey) return;

                const hasStartEnd =
                    r?.start && r?.end &&
                    Number.isFinite(Number(r.start.segmentIndex)) &&
                    Number.isFinite(Number(r.end.segmentIndex));

                if (hasStartEnd) {
                    const sI = Number(r.start.segmentIndex);
                    const eI = Number(r.end.segmentIndex);
                    const sO = Number(r.start.segmentOffset ?? 0);
                    const eO = Number(r.end.segmentOffset ?? 0);
                    items.push({
                        id: `${currentDocKey}:cited:${sI}:${sO}-${eI}:${eO}:${referenceMarkId}`,
                        type: 'cited',
                        docId: currentDocKey,
                        start: {segmentIndex: sI, segmentOffset: sO},
                        end: {segmentIndex: eI, segmentOffset: eO},
                        fragmentUid: referenceMarkId,
                    });
                    return;
                }

                // Fallback: index + offset
                if (Number.isFinite(Number(r.index))) {
                    const idx = Number(r.index);
                    const off = Number(r.offset ?? 0);
                    items.push({
                        id: `${currentDocKey}:cited:${idx}:${off}:${referenceMarkId}`,
                        type: 'cited',
                        docId: currentDocKey,
                        start: {segmentIndex: idx, segmentOffset: off},
                        end: {segmentIndex: idx, segmentOffset: off},
                        fragmentUid: referenceMarkId,
                    });
                }
            });
        });

        return items;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [yReferenceMarks, currentDocKey, version]);

    const [optimisticItems, setOptimisticItems] = useState([]);

    const addOptimisticCited = useCallback(({
                                                fragmentUid,
                                                docId,
                                                startIndex,
                                                startOffset,
                                                endIndex,
                                                endOffset,
                                                selectedText,
                                            }) => {
        const id = `${docId}:cited:${startIndex}:${startOffset}-${endIndex}:${endOffset}:${fragmentUid}`;
        setOptimisticItems(prev => {
            if (prev.some(p => p.id === id)) return prev;
            return [{
                id,
                type: 'cited',
                docId,
                content: selectedText,
                start: {segmentIndex: startIndex, segmentOffset: startOffset},
                end: {segmentIndex: endIndex, segmentOffset: endOffset},
                fragmentUid,
            }, ...prev];
        });
    }, []);

    // Merge YJS items with optimistic ones (dedupe by id)
    const merged = useMemo(() => {
        if (optimisticItems.length === 0) return citedHighlights;
        const ids = new Set(citedHighlights.map(h => h.id));
        const extra = optimisticItems.filter(h => !ids.has(h.id));
        return extra.length > 0 ? [...extra, ...citedHighlights] : citedHighlights;
    }, [citedHighlights, optimisticItems]);

    return {citedHighlights: merged, addOptimisticCited};
}
